import { useState, useEffect } from "react";
import "react-nice-dates/build/style.css";
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  CardFooter,
  Button,
  Table,
} from "reactstrap/lib";
import Rating from "@mui/material/Rating";
import { FormattedMessage } from "react-intl";
import APLRatingModal from "../Modal/APLRatingModal";
import { Tab, Tabs, Box } from "@mui/material";
import { PacmanLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { GetRatingSummary } from "../../actions/teacherQueries";

export default function APLRatings({
  ratingLevel,
  setRatingLevel,
  handleSaveEvaluationQuestions,
  activePeriodId,
  studentQuestions,
  setStudentQuestions,
  supervisorQuestions,
  setSupervisorQuestions,
  questionsToDelete,
  setQuestionsToDelete,
  loading,
  level,
  objId,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [unansweredSupervisor, setUnansweredSupervisor] = useState([]);
  const [unansweredStudent, setUnansweredStudent] = useState([]);
  const [newStudentQuestions, setNewStudentQuestions] = useState([]);
  const [newSupervisorQuestions, setNewSupervisorQuestions] = useState([]);
  const [studentRatingsForLevel, setStudentRatingsForLevel] = useState([]);
  const [supervisorRatingsForLevel, setSupervisorRatingsForLevel] = useState(
    []
  );

  const ratingSummary = GetRatingSummary(activePeriodId, level, objId);

  const ratingLimit = 5;
  const [isStudentRatingsExpanded, setIsStudentRatingsExpanded] =
    useState(false);
  const [isSupervisorRatingsExpanded, setIsSupervisorRatingsExpanded] =
    useState(false);

  // Use backend data when available
  useEffect(() => {
    if (!ratingSummary.isLoading && ratingSummary.data) {
      setStudentRatingsForLevel(ratingSummary.data.studentRatings || []);
      setUnansweredStudent(ratingSummary.data.unansweredStudentQuestions || []);
      setSupervisorRatingsForLevel(ratingSummary.data.supervisorRatings || []);
      setUnansweredSupervisor(ratingSummary.data.unansweredSupervisorQuestions || []);
    }
  }, [ratingSummary.isLoading, ratingSummary.data]);

  useEffect(() => {
    setNewStudentQuestions([...studentQuestions]);
  }, [studentQuestions]);

  useEffect(() => {
    setNewSupervisorQuestions([...supervisorQuestions]);
  }, [supervisorQuestions]);

  const toggle = () => setIsOpen(!isOpen);

  const renderInfoText = () => {
    return (
      <>
        {level === "Program" && (
          <Row className="ms-2 mb-2">
            <Col>
              <span className="small">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  color="#20a8d8"
                  className="me-1"
                />
                <FormattedMessage id="AverageOfAssessmentIsShownHereForProgram" />
              </span>
            </Col>
          </Row>
        )}
        {level === "Class" && (
          <Row className="ms-2 mb-2">
            <Col>
              <span className="small">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  color="#20a8d8"
                  className="me-1"
                />
                <FormattedMessage id="AverageOfAssessmentIsShownHereForClass" />
              </span>
            </Col>
          </Row>
        )}
      </>
    );
  };

  const renderRatingsTable = ({
    allRatings,
    visibleRatings,
    expanded,
    onToggleExpanded,
  }) => {
    if (!allRatings?.length) return null;

    return (
      <div className="d-flex flex-column">
        <Table
          responsive
          hover
          size="sm"
          className="mb-0 align-middle apl-table--borderless"
        >
          <thead className="bg-light">
            <tr>
              <th className="ps-3 py-2 text-muted" style={{ width: "70%" }}>
                <FormattedMessage id="Questions" />
              </th>
              <th className="py-2 text-muted pe-3" style={{ width: "30%" }}>
                <FormattedMessage id="Ratings" />
              </th>
            </tr>
          </thead>

          <tbody>
            {visibleRatings.map((r) => (
              <tr key={r.id}>
                <td className="ps-3">{r.question?.question}</td>
                <td>
                  <Rating
                    value={r.respons}
                    max={r.question?.responseRange}
                    readOnly
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {allRatings.length > ratingLimit && (
          <div className="text-center py-2">
            <Button
              color="link"
              size="sm"
              className="text-decoration-none"
              onClick={onToggleExpanded}
            >
              <FontAwesomeIcon
                icon={expanded ? "chevron-up" : "chevron-down"}
                className="me-1"
              />
              {expanded ? (
                <FormattedMessage id="ShowLess" />
              ) : (
                <FormattedMessage id="ShowAll" />
              )}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const selectRating = (ratingLevel) => {
    switch (ratingLevel) {
      case "student":
        const allRatingsStudent = [
          ...(studentRatingsForLevel || []),
          ...(unansweredStudent || []),
        ];

        const visibleRatingsStudent = isStudentRatingsExpanded
          ? allRatingsStudent
          : allRatingsStudent.slice(0, ratingLimit);

        return (
          <div>
            {renderInfoText()}
            {renderRatingsTable({
              allRatings: allRatingsStudent,
              visibleRatings: visibleRatingsStudent,
              expanded: isStudentRatingsExpanded,
              onToggleExpanded: () =>
                setIsStudentRatingsExpanded(!isStudentRatingsExpanded),
            })}
          </div>
        );
      case "supervisor":
        const allRatingsSupervisor = [
          ...(supervisorRatingsForLevel || []),
          ...(unansweredSupervisor || []),
        ];

        const visibleRatingsSupervisor = isSupervisorRatingsExpanded
          ? allRatingsSupervisor
          : allRatingsSupervisor.slice(0, ratingLimit);

        return (
          <div>
            {renderInfoText()}
            {renderRatingsTable({
              allRatings: allRatingsSupervisor,
              visibleRatings: visibleRatingsSupervisor,
              expanded: isSupervisorRatingsExpanded,
              onToggleExpanded: () =>
                setIsSupervisorRatingsExpanded(!isSupervisorRatingsExpanded),
            })}
          </div>
        );
      default:
        return;
    }
  };

  const handleChange = (event, newValue) => {
    setRatingLevel(newValue);
  };

  return (
    <Col lg={12}>
      <Card>
        <Box className="apl-tabs-header">
          <Tabs
            value={ratingLevel}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            className="apl-tabs"
          >
            <Tab
              label={
                <div className="d-flex align-items-center px-1">
                  <FontAwesomeIcon
                    icon="user-graduate"
                    className="me-2 text-muted"
                  />
                  <span
                    style={{
                      fontWeight: ratingLevel === "student" ? "600" : "400",
                      letterSpacing: "0.01em",
                    }}
                  >
                    <FormattedMessage id="EvaluationQuestionsStudent" />
                  </span>
                </div>
              }
              value="student"
              className="apl-tab"
            />
            <Tab
              label={
                <div className="d-flex align-items-center px-1">
                  <FontAwesomeIcon
                    icon="user-tie"
                    className="me-2 text-muted"
                  />
                  <span
                    style={{
                      fontWeight: ratingLevel === "supervisor" ? "600" : "400",
                      letterSpacing: "0.01em",
                    }}
                  >
                    <FormattedMessage id="EvaluationQuestionsSupervisor" />
                  </span>
                </div>
              }
              value="supervisor"
              disableRipple
              className="apl-tab"
            />
          </Tabs>
        </Box>

        {loading || ratingSummary.isLoading ? (
          <div
            className="sweet-loading"
            style={{ position: "relative", top: "20%", left: "50%" }}
          >
            <PacmanLoader color={"#20a8d8"} loading={true} />
          </div>
        ) : (
          <div className="apl-content">{selectRating(ratingLevel)}</div>
        )}
        <Modal isOpen={isOpen} toggle={toggle}>
          <ModalHeader>
            {" "}
            <FormattedMessage id="Ratings" />
          </ModalHeader>
          <Card style={{ marginBottom: "0", height: "34rem" }}>
            <CardBody style={{ overflowY: "auto" }}>
              <ModalBody style={{ border: "none", padding: "0" }}>
                <APLRatingModal
                  studentQuestions={studentQuestions}
                  setStudentQuestions={setStudentQuestions}
                  supervisorQuestions={supervisorQuestions}
                  setSupervisorQuestions={setSupervisorQuestions}
                  questionsToDelete={questionsToDelete}
                  setQuestionsToDelete={setQuestionsToDelete}
                  setNewStudentQuestions={setNewStudentQuestions}
                  setNewSupervisorQuestions={setNewSupervisorQuestions}
                  newStudentQuestions={newStudentQuestions}
                  newSupervisorQuestions={newSupervisorQuestions}
                />
              </ModalBody>
            </CardBody>
            <CardFooter>
              <div className="justify-content-end d-flex">
                <Button
                  outline
                  className="m-2"
                  color="secondary"
                  onClick={toggle}
                >
                  <FormattedMessage id="Cancel" />
                </Button>
                <Button
                  color="primary"
                  outline
                  className="m-2"
                  onClick={() => {
                    handleSaveEvaluationQuestions(
                      activePeriodId,
                      newStudentQuestions,
                      newSupervisorQuestions
                    );
                  }}
                >
                  <FormattedMessage id="Save" />
                </Button>
                <Button
                  color="primary"
                  outline
                  className="m-2"
                  onClick={() => {
                    handleSaveEvaluationQuestions(
                      activePeriodId,
                      newStudentQuestions,
                      newSupervisorQuestions
                    );
                    toggle();
                  }}
                >
                  <FormattedMessage id="SaveAndClose" />
                </Button>
              </div>
            </CardFooter>
          </Card>
        </Modal>
        <CardFooter>
          <div className="justify-content-end d-flex py-1">
            <Button outline color="primary" onClick={toggle}>
              <FontAwesomeIcon icon="edit" className="me-2" />
              <FormattedMessage id="EditQuestions" />
            </Button>
          </div>
        </CardFooter>
      </Card>
    </Col>
  );
}
